<template>
  <SkinnyBanner page="landing" />
  <RevContainer class="mb-16 mt-32" data-spec="body">
    <h1 class="heading-1">{{ company }}</h1>
    <h2 class="heading-2 mt-32">{{ i18n(translations.sellerPageTitle) }}</h2>

    <MainSellerInfo
      v-if="averageRate"
      :average-rate
      :country
      :country-code
      :delta-aged
    />

    <RevTabs
      class="mt-32"
      :label="i18n(translations.sellerPageTabsLabel)"
      scrollable
      variant="flexible"
    >
      <RevTabItem
        :active="activeTab === 'reviews'"
        :label="i18n(translations.sellerPageReviewsTab)"
        @click="handleTabChange('reviews')"
      />
      <RevTabItem
        :active="activeTab === 'items'"
        :label="i18n(translations.sellerPageItemsTab)"
        @click="handleTabChange('items')"
      />
      <RevTabItem
        v-if="sellerInfo"
        :active="activeTab === 'about'"
        :label="i18n(translations.sellerPageAboutTab)"
        @click="handleTabChange('about')"
      />
    </RevTabs>

    <ReviewsTab v-show="activeTab === 'reviews'" :rating />
    <ItemsTab v-show="activeTab === 'items'" />
    <AboutTab
      v-show="activeTab === 'about' && sellerInfo && countryCode"
      :country
      :country-code
      :seller-info
    />
  </RevContainer>
</template>

<script setup lang="ts">
import { showError, useHead, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevContainer } from '@ds/components/Container'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { useSkinnyBannerLoader } from '~/scopes/branding/components/SkinnyBanner/useSkinnyBanner'
import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'
import { getMetas } from '~/utils/seo/meta'

import translations from './SellerPage.translations'
import AboutTab from './components/AboutTab/AboutTab.vue'
import ItemsTab from './components/ItemsTab/ItemsTab.vue'
import MainSellerInfo from './components/MainSellerInfo/MainSellerInfo.vue'
import ReviewsTab from './components/ReviewsTab/ReviewsTab.vue'
import { useSellerInfo } from './composables/useSellerInfo'
import { useSellerRating } from './composables/useSellerRating'

const i18n = useI18n()
const logger = useLogger()
const activeTab = ref('reviews')
const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
const tracking = useTracking()
const route = useRoute()
const routeName = route.name?.toString() || ''

const { data: sellerInfo, error: sellerInfoError } = useSellerInfo()
const { data: rating } = useSellerRating()

watch(sellerInfoError, (err) => {
  if (!err) return

  logger.error('[Seller-page] Seller not found', {
    owners: ['bot-squad-content-front'],
  })

  showError({
    ...err,
    fatal: true,
  })
})

await loadSkinnyBannerData()

const company = computed(() => sellerInfo.value?.company)
const countryCode = computed(() => sellerInfo.value?.countryCode)
const country = computed(() => sellerInfo.value?.country)
const averageRate = computed(() => rating.value?.averageRate)
const deltaAged = computed(() => sellerInfo.value?.delta_aged ?? 0)

useHead({
  title: company,
  titleTemplate: `%s - ${i18n(translations.sellerPageTitle)}`,
  meta: () =>
    getMetas({
      content: i18n(translations.sellerPageMetaDescription, {
        merchantName: company.value,
      }),
      image: '/img/plp/ogImage.png',
      'og:type': 'website',
    }),
})
useAddCanonicalLink()

const handleTabChange = (tab: string) => {
  activeTab.value = tab
  tracking.trackClick({
    pageType: routeName,
    zone: tab,
  })
}
</script>
